import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import {
  Package, Layers, Plus, Trash2, CheckCircle2, AlertTriangle,
  Clock, Loader2, X, ArrowRight, Sparkles, Ship, FileText, CreditCard
} from 'lucide-react';
import { formatDate, formatCurrency } from '../lib/utils';
import CustomsDeclarationForm from './CustomsDeclarationForm';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-800', icon: Layers },
  submitted: { label: 'Soumis', color: 'bg-blue-100 text-blue-800', icon: Clock },
  approved: { label: 'Approuvé', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  preparing: { label: 'En préparation', color: 'bg-amber-100 text-amber-800', icon: Package },
  shipped: { label: 'Expédié', color: 'bg-purple-100 text-purple-800', icon: Ship },
  delivered: { label: 'Livré', color: 'bg-slate-100 text-slate-700', icon: CheckCircle2 },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: X },
};

const STORAGE_BADGE = {
  ok: null,
  warning: { label: 'Bientôt frais', color: 'bg-amber-100 text-amber-800' },
  fees: { label: 'Frais en cours', color: 'bg-orange-100 text-orange-800' },
  critical: { label: 'Action urgente', color: 'bg-red-100 text-red-800' },
};

export default function GroupageSection() {
  const [packages, setPackages] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupNotes, setGroupNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [selectingOption, setSelectingOption] = useState(false);
  const [customsOpen, setCustomsOpen] = useState(false);
  const [customsGroup, setCustomsGroup] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, groupsRes] = await Promise.all([
        api.get('/client/packages-with-storage'),
        api.get('/client/groups'),
      ]);
      setPackages(pkgRes.data || []);
      setGroups(groupsRes.data || []);
    } catch {
      toast.error('Erreur chargement groupages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Available packages = not shipped + not in a group
  const availablePackages = packages.filter(p =>
    !p.group_id && !['shipped', 'delivered', 'in_transit'].includes(p.status)
  );

  // Group available packages by destination for multi-destination warning
  const destinationsOfSelected = new Set(
    selectedIds.map(id => packages.find(p => p.id === id)?.destination_country).filter(Boolean)
  );
  const multiDestination = destinationsOfSelected.size > 1;

  // Compute totals for selected
  const selectedPkgs = selectedIds.map(id => packages.find(p => p.id === id)).filter(Boolean);
  const totalWeight = selectedPkgs.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const openCreator = () => {
    setSelectedIds([]);
    setGroupName('');
    setGroupNotes('');
    setCreatorOpen(true);
  };

  const createGroup = async () => {
    if (selectedIds.length < 2) { toast.error('Sélectionnez au moins 2 colis'); return; }
    if (multiDestination) { toast.error('Tous les colis doivent avoir la même destination'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/client/groups', {
        package_ids: selectedIds,
        name: groupName || undefined,
        notes: groupNotes || undefined,
      });
      toast.success(`Groupage créé ! Économie : ${formatCurrency(res.data.savings || 0)}`);
      setCreatorOpen(false);
      await fetchData();
      setCurrentGroup(res.data);
      setSelectingOption(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur création');
    } finally {
      setSubmitting(false);
    }
  };

  const chooseShippingOption = async (option) => {
    if (!currentGroup) return;
    try {
      await api.patch(`/client/groups/${currentGroup.id}`, { chosen_shipping_option: option });
      toast.success('Transporteur enregistré. Remplissez maintenant la déclaration douanière.');
      setSelectingOption(false);
      // Open customs declaration step
      const refreshed = await api.get(`/client/groups/${currentGroup.id}`);
      setCustomsGroup(refreshed.data);
      setCustomsOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const onCustomsComplete = async () => {
    if (!customsGroup) return;
    try {
      // For client-created draft groups, also submit them. For admin-created approved groups, just save the customs.
      if (customsGroup.status === 'draft' && !customsGroup.created_by_admin) {
        await api.post(`/client/groups/${customsGroup.id}/submit`);
        toast.success('Groupage soumis ! Notre équipe vous contactera sous 24h.');
      } else {
        toast.success('Déclaration enregistrée ! Vous pouvez maintenant régler.');
      }
      setCustomsOpen(false);
      setCustomsGroup(null);
      setCurrentGroup(null);
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement');
    }
  };

  const openCustomsForExistingGroup = async (group) => {
    setCustomsGroup(group);
    setCustomsOpen(true);
  };

  const cancelGroup = async (groupId) => {
    if (!window.confirm('Annuler ce groupage ? Les colis redeviendront disponibles.')) return;
    try {
      await api.delete(`/client/groups/${groupId}`);
      toast.success('Groupage annulé');
      await fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const payGroup = useCallback(async (groupId) => {
    try {
      toast.loading('Préparation du paiement…', { id: `pay-${groupId}` });
      const res = await api.post('/payments/group-checkout', {
        group_id: groupId,
        origin_url: window.location.origin,
      });
      toast.dismiss(`pay-${groupId}`);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Lien de paiement indisponible');
      }
    } catch (err) {
      toast.dismiss(`pay-${groupId}`);
      toast.error(err?.response?.data?.detail || 'Erreur lors du paiement');
    }
  }, []);

  // Auto-trigger payment when arriving from email link: /dashboard?action=pay_group&group_id=...
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'pay_group' && params.get('group_id')) {
      const gid = params.get('group_id');
      // Clean the URL so refresh doesn't re-trigger
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      // If group missing customs, open the customs form first instead of trying payment
      const g = groups.find(x => x.id === gid);
      if (g && !g.customs_declaration && g.status === 'approved' && !g.paid) {
        setCustomsGroup(g);
        setCustomsOpen(true);
        toast.info('Veuillez d\'abord remplir la déclaration douanière');
        return;
      }
      payGroup(gid);
    }
  }, [loading, payGroup, groups]);

  if (loading) {
    return <div className="flex justify-center p-10"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header info card */}
      <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-1 flex items-center gap-2">
                <Layers className="w-5 h-5 text-blue-600" />
                Groupez vos colis, économisez gros
              </h3>
              <p className="text-sm text-slate-600">
                Regroupez plusieurs colis en un seul envoi pour économiser jusqu'à <strong>60%</strong> sur les frais d'expédition.
              </p>
              <p className="text-xs text-slate-500 mt-2">
                ⏱️ <strong>Stockage gratuit pendant 30 jours.</strong> Au-delà, 0,50€/colis/jour. Limite à 60 jours.
              </p>
            </div>
            {availablePackages.length >= 2 && (
              <Button onClick={openCreator} className="bg-blue-600 hover:bg-blue-700" data-testid="grp-new-btn">
                <Plus className="w-4 h-4 mr-2" /> Créer un groupage
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active groups */}
      {groups.filter(g => g.status !== 'cancelled').length > 0 && (
        <div>
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-3">Mes groupages</h3>
          <div className="space-y-3">
            {groups.filter(g => g.status !== 'cancelled').map(g => {
              const cfg = STATUS_CONFIG[g.status] || STATUS_CONFIG.draft;
              const Icon = cfg.icon;
              return (
                <Card key={g.id} className="border-slate-200" data-testid={`group-${g.id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-heading font-bold text-slate-900">{g.name}</h4>
                          <Badge className={cfg.color}>
                            <Icon className="w-3 h-3 mr-1" /> {cfg.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">
                          {g.package_ids?.length || 0} colis · {g.total_weight}kg · {g.destination_country} · créé {formatDate(g.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Économies</p>
                        <p className="font-heading font-bold text-2xl text-emerald-600">
                          {formatCurrency(g.savings || 0)}
                        </p>
                      </div>
                    </div>
                    {g.chosen_shipping_option && (
                      <div className="bg-slate-50 rounded-lg p-3 text-sm">
                        <strong>Transporteur choisi :</strong> {g.chosen_shipping_option.name} ({g.chosen_shipping_option.carrier})
                        — <strong>{formatCurrency(g.chosen_shipping_option.price)}</strong>
                        {g.chosen_shipping_option.management_fee !== undefined && (
                          <span className="text-slate-600"> + {formatCurrency(g.chosen_shipping_option.management_fee)} de frais</span>
                        )}
                        {g.admin_price_override && (
                          <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded text-xs text-purple-900">
                            ✏️ <strong>Prix ajusté par notre équipe</strong>
                            {g.admin_price_note && <p className="mt-1 italic">« {g.admin_price_note} »</p>}
                          </div>
                        )}
                      </div>
                    )}
                    {g.tracking_number && (
                      <div className="mt-2 text-sm text-blue-700">
                        📦 Tracking : <strong>{g.tracking_number}</strong>
                      </div>
                    )}
                    {['draft', 'submitted'].includes(g.status) && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {g.status === 'draft' && !g.chosen_shipping_option && (
                          <Button size="sm" onClick={() => { setCurrentGroup(g); setSelectingOption(true); }}>
                            1. Choisir le transporteur <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        )}
                        {g.status === 'draft' && g.chosen_shipping_option && !g.customs_declaration && (
                          <Button size="sm" onClick={() => openCustomsForExistingGroup(g)} className="bg-amber-600 hover:bg-amber-700">
                            <FileText className="w-4 h-4 mr-1" /> 2. Déclaration douanière
                          </Button>
                        )}
                        {g.customs_declaration && (
                          <Badge className="bg-emerald-100 text-emerald-800 self-center">
                            <FileText className="w-3 h-3 mr-1" /> Douane OK ({g.customs_declaration.items?.length || 0} articles, {g.customs_declaration.total_value}€)
                          </Badge>
                        )}
                        <Button size="sm" variant="outline" onClick={() => cancelGroup(g.id)} className="text-red-600">
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Annuler
                        </Button>
                      </div>
                    )}
                    {g.status === 'approved' && !g.paid && (
                      <div className="mt-3 p-4 rounded-lg bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-300">
                        <div className="flex flex-col gap-3">
                          <div>
                            <p className="text-sm font-semibold text-emerald-900">💳 Prêt à expédier — paiement requis</p>
                            <p className="text-xs text-slate-600 mt-1">
                              Montant : <strong className="text-emerald-700 text-base">{formatCurrency(g.admin_price_total || g.chosen_shipping_option?.price || 0)}</strong>
                            </p>
                            {g.admin_price_note && (
                              <p className="text-xs text-slate-600 italic mt-1">« {g.admin_price_note} »</p>
                            )}
                          </div>
                          {!g.customs_declaration ? (
                            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm">
                              <p className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                Étape obligatoire : déclaration douanière
                              </p>
                              <p className="text-xs text-amber-800 mb-3">Remplissez la déclaration des articles avant de pouvoir régler.</p>
                              <Button
                                onClick={() => openCustomsForExistingGroup(g)}
                                className="w-full bg-amber-600 hover:bg-amber-700"
                                data-testid={`customs-group-${g.id}`}
                              >
                                <FileText className="w-4 h-4 mr-2" /> Remplir la déclaration
                              </Button>
                            </div>
                          ) : (
                            <>
                              <Badge className="bg-emerald-100 text-emerald-800 self-start">
                                <FileText className="w-3 h-3 mr-1" /> Douane OK ({g.customs_declaration.items?.length || 0} articles, {g.customs_declaration.total_value}€)
                              </Badge>
                              <Button
                                size="lg"
                                onClick={() => payGroup(g.id)}
                                className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg"
                                data-testid={`pay-group-${g.id}`}
                              >
                                <CreditCard className="w-4 h-4 mr-2" /> Payer {formatCurrency(g.admin_price_total || g.chosen_shipping_option?.price || 0)}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {g.paid && (
                      <Badge className="bg-emerald-600 text-white mt-3">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Payé
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Available packages */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Package className="w-5 h-5" /> Colis disponibles pour groupage ({availablePackages.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {availablePackages.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              Aucun colis disponible. Vos prochains colis reçus à l'entrepôt apparaîtront ici.
            </p>
          ) : (
            <div className="space-y-2">
              {availablePackages.map(p => {
                const badge = STORAGE_BADGE[p.storage_status];
                return (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`avail-pkg-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-slate-900 truncate">{p.description}</p>
                        <Badge variant="outline" className="text-xs">{p.weight}kg</Badge>
                        <Badge variant="outline" className="text-xs">{p.destination_country}</Badge>
                        {badge && <Badge className={`text-xs ${badge.color}`}>{badge.label}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Stockage : {p.storage_days} jour{p.storage_days > 1 ? 's' : ''}
                        {p.storage_status === 'ok' && ` · gratuit pendant ${p.days_until_fees}j`}
                        {p.storage_fee > 0 && ` · frais : ${formatCurrency(p.storage_fee)}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Creator modal */}
      <Dialog open={creatorOpen} onOpenChange={setCreatorOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" /> Créer un groupage
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Nom (optionnel)</Label>
              <Input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Ex: Achats janvier" />
            </div>

            <div>
              <Label>Sélectionnez les colis à grouper ({selectedIds.length} sélectionnés)</Label>
              <div className="space-y-2 mt-2 max-h-72 overflow-y-auto border rounded-lg p-2">
                {availablePackages.length === 0 ? (
                  <p className="text-center text-slate-500 py-6 text-sm">Aucun colis disponible</p>
                ) : availablePackages.map(p => {
                  const selected = selectedIds.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleSelect(p.id)}
                      data-testid={`sel-${p.id}`}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-colors flex items-start gap-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                        {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-slate-900 truncate">{p.description}</p>
                        <p className="text-xs text-slate-500">
                          {p.weight}kg · {p.destination_country} · {p.storage_days}j de stockage
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {multiDestination && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-900">
                  Vos colis vont vers <strong>{destinationsOfSelected.size} destinations différentes</strong>. Un groupage doit avoir une seule destination.
                </p>
              </div>
            )}

            {selectedIds.length >= 2 && !multiDestination && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-slate-700 mb-1">
                  <strong>{selectedIds.length} colis</strong> · Poids total : <strong>{totalWeight.toFixed(2)} kg</strong>
                </p>
                <p className="text-xs text-emerald-700">
                  💡 Économies estimées après création du groupage. Le détail s'affichera ensuite.
                </p>
              </div>
            )}

            <div>
              <Label>Notes (optionnel)</Label>
              <Textarea value={groupNotes} onChange={e => setGroupNotes(e.target.value)} placeholder="Préférences d'emballage, instructions..." rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreatorOpen(false)} disabled={submitting}>Annuler</Button>
              <Button
                onClick={createGroup}
                disabled={selectedIds.length < 2 || multiDestination || submitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-group-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer le groupage
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shipping option selection modal */}
      <Dialog open={selectingOption} onOpenChange={(open) => { if (!open) { setSelectingOption(false); setCurrentGroup(null); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Choisissez votre transporteur</DialogTitle>
          </DialogHeader>
          {currentGroup && (
            <div className="space-y-3 pt-2">
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <strong>{currentGroup.package_ids?.length} colis</strong> · {currentGroup.total_weight}kg · vers {currentGroup.destination_country}
              </div>
              <div className="space-y-2">
                {(currentGroup.available_shipping_options || []).map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => chooseShippingOption(opt)}
                    className="w-full text-left p-4 rounded-xl border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all flex items-center justify-between"
                    data-testid={`opt-${opt.id}`}
                  >
                    <div>
                      <p className="font-semibold text-slate-900">{opt.name}</p>
                      <p className="text-xs text-slate-500">{opt.carrier} · {opt.delivery_time}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading font-bold text-xl text-slate-900">{formatCurrency(opt.price)}</p>
                      <p className="text-xs text-slate-500">+ {formatCurrency(opt.management_fee)} de frais</p>
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 italic">
                Après votre choix, notre équipe validera votre groupage sous 24h ouvrées.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Customs declaration modal */}
      <Dialog open={customsOpen} onOpenChange={(open) => { if (!open) { setCustomsOpen(false); setCustomsGroup(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" /> Déclaration douanière du groupage
            </DialogTitle>
          </DialogHeader>
          {customsGroup && (
            <div className="pt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
                <strong>{customsGroup.package_ids?.length} colis</strong> · {customsGroup.total_weight}kg · vers {customsGroup.destination_country}
                <p className="text-xs mt-1">Listez tous les articles présents dans ce groupage (consolidés en une seule déclaration).</p>
              </div>
              <CustomsDeclarationForm
                mode="group"
                groupId={customsGroup.id}
                packageDescription={customsGroup.name}
                existingDeclaration={customsGroup.customs_declaration}
                onComplete={onCustomsComplete}
                onCancel={() => { setCustomsOpen(false); setCustomsGroup(null); }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
